import { useState } from 'react';
import { hasProFeatures } from '@/utils/entitlements';
import {
  useMarketValue,
  useTitleCheck,
  useAuction,
  useWarranty,
  useMaintenance,
  useCommonRepairs,
  useSalesHistory,
  useEntitlement,
} from '@/hooks/useProApi';
import {
  MarketValueIcon,
  TitleCheckIcon,
  AuctionIcon,
  WarrantyIcon,
  MaintenanceIcon,
  RepairsIcon,
  SalesHistoryIcon,
} from './icons';
import { MarketValueTab } from './tabs/MarketValueTab';
import { TitleCheckTab } from './tabs/TitleCheckTab';
import { AuctionTab } from './tabs/AuctionTab';
import { WarrantyTab } from './tabs/WarrantyTab';
import { MaintenanceTab } from './tabs/MaintenanceTab';
import { RepairsTab } from './tabs/RepairsTab';
import { SalesHistoryTab } from './tabs/SalesHistoryTab';
import type { NhtsaVinDecodeResult } from '@/types';

const TABS = [
  { id: 'market-value', label: 'Market Value', Icon: MarketValueIcon },
  { id: 'title-check', label: 'Title Check', Icon: TitleCheckIcon },
  { id: 'auction', label: 'Auction', Icon: AuctionIcon },
  { id: 'warranty', label: 'Warranty', Icon: WarrantyIcon },
  { id: 'maintenance', label: 'Maintenance', Icon: MaintenanceIcon },
  { id: 'repairs', label: 'Repairs', Icon: RepairsIcon },
  { id: 'sales-history', label: 'Sales History', Icon: SalesHistoryIcon },
] as const;

type TabId = typeof TABS[number]['id'];

interface VehicleDataTabsProps {
  vin: string;
  nhtsaVehicleInfo: NhtsaVinDecodeResult | undefined;
}

export function VehicleDataTabs({ vin, nhtsaVehicleInfo }: VehicleDataTabsProps) {
  const { data: entitlement } = useEntitlement();
  const isPro = hasProFeatures(entitlement?.plan);
  const [activeTab, setActiveTab] = useState<TabId>('market-value');

  // Click-to-load state for paid APIs
  const [loadMarketValue, setLoadMarketValue] = useState(false);
  const [loadTitleCheck, setLoadTitleCheck] = useState(false);
  const [loadAuction, setLoadAuction] = useState(false);
  const [loadWarranty, setLoadWarranty] = useState(false);
  const [loadMaintenance, setLoadMaintenance] = useState(false);
  const [loadRepairs, setLoadRepairs] = useState(false);
  const [loadSalesHistory, setLoadSalesHistory] = useState(false);

  // All hooks called at container level so cache persists across tab switches
  const marketValueQuery = useMarketValue(vin, loadMarketValue);
  const titleCheckQuery = useTitleCheck(vin, loadTitleCheck);
  const auctionQuery = useAuction(vin, loadAuction);
  const warrantyQuery = useWarranty(
    nhtsaVehicleInfo?.year?.toString() || null,
    nhtsaVehicleInfo?.make || null,
    nhtsaVehicleInfo?.model || null,
    loadWarranty && !!nhtsaVehicleInfo
  );
  const maintenanceQuery = useMaintenance(vin, loadMaintenance);
  const repairsQuery = useCommonRepairs(vin, loadRepairs);
  const salesHistoryQuery = useSalesHistory(vin, loadSalesHistory);

  return (
    <div>
      {/* Tab Bar */}
      <div className="flex overflow-x-auto -mx-6 px-6 border-b border-white/10 gap-1">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-3 py-3 text-sm font-medium whitespace-nowrap transition relative flex-shrink-0
              ${activeTab === id ? 'text-white' : 'text-gray-500 hover:text-gray-300'}`}
          >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-pink-500 text-white leading-none">
              PRO
            </span>
            {activeTab === id && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#6d7cff] to-[#e866dd]" />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content Area */}
      <div className="h-[50vh] min-h-[550px] overflow-y-auto mt-4">
        {activeTab === 'market-value' && (
          <MarketValueTab
            query={marketValueQuery}
            loadEnabled={loadMarketValue}
            onLoad={() => setLoadMarketValue(true)}
            disabled={!isPro}
          />
        )}
        {activeTab === 'title-check' && (
          <TitleCheckTab
            query={titleCheckQuery}
            loadEnabled={loadTitleCheck}
            onLoad={() => setLoadTitleCheck(true)}
            disabled={!isPro}
          />
        )}
        {activeTab === 'auction' && (
          <AuctionTab
            query={auctionQuery}
            loadEnabled={loadAuction}
            onLoad={() => setLoadAuction(true)}
            disabled={!isPro}
          />
        )}
        {activeTab === 'warranty' && (
          <WarrantyTab
            query={warrantyQuery}
            loadEnabled={loadWarranty}
            onLoad={() => setLoadWarranty(true)}
            disabled={!isPro}
          />
        )}
        {activeTab === 'maintenance' && (
          <MaintenanceTab
            query={maintenanceQuery}
            loadEnabled={loadMaintenance}
            onLoad={() => setLoadMaintenance(true)}
            disabled={!isPro}
          />
        )}
        {activeTab === 'repairs' && (
          <RepairsTab
            query={repairsQuery}
            loadEnabled={loadRepairs}
            onLoad={() => setLoadRepairs(true)}
            disabled={!isPro}
          />
        )}
        {activeTab === 'sales-history' && (
          <SalesHistoryTab
            query={salesHistoryQuery}
            loadEnabled={loadSalesHistory}
            onLoad={() => setLoadSalesHistory(true)}
            disabled={!isPro}
          />
        )}
      </div>
    </div>
  );
}
